import { type DivIcon, divIcon } from "leaflet";
import _ from "lodash";

import type Light from "@/models/light";

interface LegendIcon {
    position: {
        lat: number;
        lng: number;
    };
    icon: DivIcon;
}

interface IdIconOptions {
    /**
     * Whether to truncate the BLE ID down to its last four characters.
     */
    showLastBleId: boolean;
    /**
     * The configured positioning type, e.g. `VLC`, `BLE`, or `VLC + BLE`.
     */
    positioningType?: string;
}

/**
 * Builds the flag badge cluster rendered above a light.
 *
 * @param light - The light to build flag badges for.
 * @returns The badge position and icon, or null when the light has no flags.
 */
export const createFlagsIcon = (light: Light): LegendIcon | null => {
    if (light.flags.length === 0) return null;

    const orderedFlags = _.orderBy(light.flags, ["index"], ["asc"]);

    const html = _.reduce(
        orderedFlags,
        (markup, flag) => {
            const style =
                flag.icon === "triangle"
                    ? `border-bottom-color: ${flag.color}`
                    : `background-color: ${flag.color}`;

            return `${markup}<div key="flag-${light.id}" class="flag-${flag.icon}" style="${style}"></div>`;
        },
        "",
    );

    return {
        position: {
            lat: light.bounds.getNorth() - 0.15,
            lng: light.bounds.getWest() - 0.2,
        },
        icon: divIcon({
            className: "label",
            html: `<div class="flags-wrapper">${html}</div>`,
        }),
    };
};

/**
 * Builds the identifier label rendered beside a light.
 *
 * @param light - The light to label.
 * @param options - The display options controlling which identifiers are shown.
 * @returns The label position and icon.
 */
export const createIdIcon = (
    light: Light,
    { showLastBleId, positioningType }: IdIconOptions,
): LegendIcon => {
    const fullBleId = _.toString(light.bleId);
    const bleId =
        showLastBleId && fullBleId !== "0"
            ? `...${fullBleId.slice(fullBleId.length - 4)}`
            : fullBleId;

    let html;

    switch (positioningType) {
        case "VLC":
            html = `${light.id}`;
            break;
        case "BLE":
            html = `${bleId}`;
            break;
        default:
            html = `${light.id}</br>${bleId}`;
            break;
    }

    return {
        position: {
            lat: light.point.lat + 0.1,
            lng: light.bounds.getEast() + 0.09,
        },
        icon: divIcon({
            className: "label",
            html,
        }),
    };
};
